import React, { useState, useMemo } from 'react';
import {
  PROFESSIONAL_CATEGORIES,
  PROFESSIONAL_CATEGORY_DESCRIPTIONS,
  PROFESSIONAL_FOCUS,
  PROFESSIONAL_TYPE_LABELS,
  PROFESSIONAL_TYPE_DESCRIPTIONS,
  PROFESSIONAL_TYPE_EXTENDED,
  CANONICAL_PROFESSIONAL_TYPES,
  type ProfessionalType,
  type ProfessionalCategoryId,
  type ProfessionalFocus,
} from '../types/professionalRates';

interface Props {
  onSelect: (type: ProfessionalType) => void;
  onSkip?: () => void;
  selectedType?: ProfessionalType;
}

const CATEGORY_COLORS: Record<ProfessionalCategoryId, string> = {
  ld:      'bg-blue-100 text-blue-800 border-blue-200',
  psa:     'bg-purple-100 text-purple-800 border-purple-200',
  welzijn: 'bg-green-100 text-green-800 border-green-200',
  trauma:  'bg-red-100 text-red-800 border-red-200',
  arbo:    'bg-slate-200 text-slate-800 border-slate-300',
};

const CATEGORY_BADGE: Record<ProfessionalCategoryId, string> = {
  ld:      'bg-blue-500',
  psa:     'bg-purple-500',
  welzijn: 'bg-green-500',
  trauma:  'bg-red-500',
  arbo:    'bg-slate-600',
};

const CATEGORY_SECTION: Record<ProfessionalCategoryId, { border: string; header: string; label: string }> = {
  ld:      { border: 'border-blue-200',   header: 'bg-blue-50 border-b border-blue-200',   label: 'text-blue-700' },
  psa:     { border: 'border-purple-200', header: 'bg-purple-50 border-b border-purple-200', label: 'text-purple-700' },
  welzijn: { border: 'border-green-200',  header: 'bg-green-50 border-b border-green-200',  label: 'text-green-700' },
  trauma:  { border: 'border-red-200',    header: 'bg-red-50 border-b border-red-200',      label: 'text-red-700' },
  arbo:    { border: 'border-slate-300',  header: 'bg-slate-100 border-b border-slate-300', label: 'text-slate-700' },
};

function getCategoryForType(type: ProfessionalType): ProfessionalCategoryId | null {
  for (const [id, cat] of Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]) {
    if ((cat.types as ProfessionalType[]).includes(type)) return id;
  }
  return null;
}

export const ProfessionalTypePicker: React.FC<Props> = ({ onSelect, onSkip, selectedType }) => {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<ProfessionalCategoryId | 'all'>('all');
  const [activeFocus, setActiveFocus] = useState<ProfessionalFocus | 'all'>('all');
  const [highlighted, setHighlighted] = useState<ProfessionalType | null>(null);

  const filtered = useMemo(() => {
    return CANONICAL_PROFESSIONAL_TYPES.filter((type) => {
      if (activeCategory !== 'all') {
        const cat = getCategoryForType(type);
        if (cat !== activeCategory) return false;
      }
      if (activeFocus !== 'all') {
        const focus = PROFESSIONAL_FOCUS[type];
        if (focus !== activeFocus && focus !== 'hybride') return false;
      }
      if (query.trim()) {
        const q = query.toLowerCase();
        const label = PROFESSIONAL_TYPE_LABELS[type]?.toLowerCase() ?? '';
        const desc = PROFESSIONAL_TYPE_DESCRIPTIONS[type]?.toLowerCase() ?? '';
        if (!label.includes(q) && !desc.includes(q)) return false;
      }
      return true;
    });
  }, [query, activeCategory, activeFocus]);

  const handleCardClick = (type: ProfessionalType) => {
    if (highlighted === type) {
      // Second click confirms selection
      onSelect(type);
    } else {
      setHighlighted(type);
    }
  };

  const highlightedInfo = highlighted ? PROFESSIONAL_TYPE_EXTENDED[highlighted] : null;
  const highlightedCategory = highlighted ? getCategoryForType(highlighted) : null;

  return (
    <div className="space-y-5">
      {/* Search input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Zoek een type professional
        </label>
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHighlighted(null); }}
            placeholder="Bijv. mediator, coach, teamcoach..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          {query && (
            <button
              onClick={() => { setQuery(''); setHighlighted(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Category tabs */}
      <div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setActiveCategory('all'); setHighlighted(null); }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              activeCategory === 'all'
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
            }`}
          >
            Alle professionals
          </button>
          {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([id, cat]) => (
            <button
              key={id}
              onClick={() => { setActiveCategory(activeCategory === id ? 'all' : id); setHighlighted(null); }}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeCategory === id
                  ? `${CATEGORY_COLORS[id]} border-current`
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
              }`}
            >
              {cat.shortLabel}
            </button>
          ))}
        </div>
      </div>

      {/* Category description */}
      {activeCategory !== 'all' && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${CATEGORY_COLORS[activeCategory]}`}>
          <span className="font-semibold">{PROFESSIONAL_CATEGORIES[activeCategory].label}</span>
          <p className="mt-1">{PROFESSIONAL_CATEGORY_DESCRIPTIONS[activeCategory]}</p>
        </div>
      )}

      {/* Focus filter */}
      <div className="flex gap-2 items-center">
        <span className="text-sm text-gray-500">Gericht op:</span>
        {(['all', 'organisatie', 'medewerker'] as const).map((f) => (
          <button
            key={f}
            onClick={() => { setActiveFocus(f); setHighlighted(null); }}
            className={`px-3 py-1 rounded text-sm border transition-colors ${
              activeFocus === f
                ? 'bg-gray-700 text-white border-gray-700'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
            }`}
          >
            {f === 'all' ? 'Alle' : f === 'organisatie' ? 'Organisatie' : 'Medewerkers'}
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="text-xs text-gray-400">
        {filtered.length} professional{filtered.length !== 1 ? 's' : ''} gevonden
      </p>

      {/* Type cards */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">
          Geen professionals gevonden. Pas uw zoekopdracht aan.
        </div>
      ) : activeCategory === 'all' && !query.trim() ? (
        /* Grouped by category */
        <div className="space-y-4">
          {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => {
            const catTypes = cat.types.filter(t => filtered.includes(t));
            if (catTypes.length === 0) return null;
            const sec = CATEGORY_SECTION[catId];
            return (
              <div key={catId} className={`rounded-lg border ${sec.border} overflow-hidden`}>
                <div className={`px-4 py-2 flex items-center gap-2 ${sec.header}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${CATEGORY_BADGE[catId]}`} />
                  <span className={`text-sm font-semibold ${sec.label}`}>{cat.label}</span>
                </div>
                <div className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {catTypes.map((type) => {
                    const isHighlighted = highlighted === type;
                    const isSelected = selectedType === type;
                    return (
                      <button
                        key={type}
                        onClick={() => handleCardClick(type)}
                        className={`relative text-left p-3 rounded-lg border-2 transition-all ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : isHighlighted
                            ? 'border-blue-400 bg-blue-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-400 hover:shadow-sm'
                        }`}
                      >
                        <span className="block text-sm font-medium text-gray-900 leading-snug">
                          {PROFESSIONAL_TYPE_LABELS[type]}
                        </span>
                        {PROFESSIONAL_TYPE_EXTENDED[type]?.nobco && (
                          <span className="mt-1 inline-block text-xs text-blue-600 font-medium">NOBCO/EIA</span>
                        )}
                        {isHighlighted && !isSelected && (
                          <span className="mt-2 block text-xs text-blue-600 font-medium">Klik nogmaals om te selecteren →</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Flat grid (filtered or searched) */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((type) => {
            const catId = getCategoryForType(type);
            const isHighlighted = highlighted === type;
            const isSelected = selectedType === type;
            return (
              <button
                key={type}
                onClick={() => handleCardClick(type)}
                className={`relative text-left p-3 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-blue-600 bg-blue-50'
                    : isHighlighted
                    ? 'border-blue-400 bg-blue-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-gray-400 hover:shadow-sm'
                }`}
              >
                {catId && (
                  <span className={`absolute top-2 right-2 w-2 h-2 rounded-full ${CATEGORY_BADGE[catId]}`} />
                )}
                <span className="block text-sm font-medium text-gray-900 leading-snug pr-3">
                  {PROFESSIONAL_TYPE_LABELS[type]}
                </span>
                {PROFESSIONAL_TYPE_EXTENDED[type]?.nobco && (
                  <span className="mt-1 inline-block text-xs text-blue-600 font-medium">NOBCO/EIA</span>
                )}
                {isHighlighted && !isSelected && (
                  <span className="mt-2 block text-xs text-blue-600 font-medium">Klik nogmaals om te selecteren →</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Description panel for highlighted type */}
      {highlighted && (
        <div className={`rounded-lg border p-4 space-y-3 ${highlightedCategory ? CATEGORY_COLORS[highlightedCategory] : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-base">
              {PROFESSIONAL_TYPE_LABELS[highlighted]}
              {PROFESSIONAL_TYPE_EXTENDED[highlighted]?.nobco && (
                <span className="ml-2 text-xs font-medium bg-white bg-opacity-60 rounded px-1.5 py-0.5">NOBCO/EIA</span>
              )}
            </h3>
            <button onClick={() => setHighlighted(null)} className="text-sm opacity-60 hover:opacity-100 shrink-0">✕</button>
          </div>
          <p className="text-sm">{PROFESSIONAL_TYPE_DESCRIPTIONS[highlighted]}</p>
          {highlightedInfo && (
            <>
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide opacity-70">Wanneer inzetten</span>
                <p className="mt-0.5 text-sm">{highlightedInfo.wanneer}</p>
              </div>
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide opacity-70">Meerwaarde voor uw organisatie</span>
                <p className="mt-0.5 text-sm">{highlightedInfo.meerwaarde}</p>
              </div>
            </>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => onSelect(highlighted)}
              className="px-4 py-2 bg-white text-gray-900 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              Selecteer {PROFESSIONAL_TYPE_LABELS[highlighted]}
            </button>
          </div>
        </div>
      )}

      {/* Skip link */}
      {onSkip && (
        <div className="pt-2 text-center">
          <button
            onClick={onSkip}
            className="text-sm text-gray-400 hover:text-gray-600 underline"
          >
            Verder zonder type te selecteren
          </button>
        </div>
      )}
    </div>
  );
};
